import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule } from '@ionic/angular';
import { ReprintItem } from '../../../models/printing/ReprintItem.interface';

/**
 * app-item-reprint-list
 *
 * Presentational per-item reprint list: one row per physical item (e.g.
 * "BOX 1 of 3"), each with independent Labels / Copier-sheets steppers seeded by
 * the parent. There is ONE print button for the whole list, not one per row: the
 * user adjusts each item's counts and prints them all in a single job. Sending
 * the whole set is what keeps the printed "X of N" numbering correct — the print
 * API derives N from the item array, so a per-item print would read "1 of 1".
 * Counts have a minimum of 1 (the API requires ≥1 of each item sent), so an item
 * can't currently be zeroed out — a reprint prints at least one of every item.
 *
 * Pure/dumb — it mutates the passed-in rows' copy counts in place, emits
 * {@link changed} on every count change (so the parent can re-arm a printed
 * batch) and {@link reprintAll} when the button is pressed; the parent owns how
 * the print is actually submitted and drives the button state via {@link status}.
 *
 * A row can be set to 0 to leave that item out. The whole set is still SENT as
 * one job — zero-copy items are dropped when the QR block is built (see
 * normalizeCopies) — so the survivors keep their stored numbering and a lone
 * reprint still reads "BOX 2 of 3". This is what stops an operator reprinting a
 * whole pallet to recover one lost label.
 *
 * Shared by the mobile "Done" screen ({@link LogFinishedComponent}) and the
 * desktop reprint modal ({@link ReprintLabelsModalComponent}) so both look and
 * behave identically. Standalone + self-styled: the mobile stepper styles live
 * under the global `.receiving-mobile` scope, which does not reach the desktop
 * modal, so this component carries its own copy of those styles.
 */
@Component({
  selector: 'app-item-reprint-list',
  standalone: true,
  imports: [CommonModule, IonicModule],
  templateUrl: './item-reprint-list.component.html',
  styleUrls: ['./item-reprint-list.component.css'],
})
export class ItemReprintListComponent {
  /** Rows to render, in item order. Mutated in place as the user adjusts counts. */
  @Input() items: ReprintItem[] = [];
  /** Whether the Labels stepper is shown (a label-printing doc is selected). */
  @Input() printsLabels = false;
  /** Whether the Copier-sheets stepper is shown (a sheet-printing doc is selected). */
  @Input() printsSheets = false;
  /** Small uppercase heading above the list. */
  @Input() sectionLabel = 'Reprint items';
  /** Button verb in its ready state ("Reprint" / "Print"). */
  @Input() actionVerb = 'Reprint';
  /** Button verb in its done state ("Reprinted" / "Printed"). */
  @Input() actionVerbDone = 'Reprinted';
  /** Overall batch state, owned by the parent, driving the single button. */
  @Input() status: 'idle' | 'sending' | 'printed' = 'idle';

  /** Emitted when the single print button is pressed; the parent submits the job. */
  @Output() reprintAll = new EventEmitter<void>();
  /** Emitted whenever a count changes (stepper or clear-all) so the parent re-arms. */
  @Output() changed = new EventEmitter<void>();

  // Counts floor at 0, which means "leave this item out". The Print API still
  // requires ≥1 for every item it receives; the zeroed rows are dropped when the
  // request is built, not sent as zeros.
  public incLabel(row: ReprintItem): void { row.labelCopies += 1; this._changed(); }
  public decLabel(row: ReprintItem): void { row.labelCopies = Math.max(0, row.labelCopies - 1); this._changed(); }
  public incSheet(row: ReprintItem): void { row.sheetCopies += 1; this._changed(); }
  public decSheet(row: ReprintItem): void { row.sheetCopies = Math.max(0, row.sheetCopies - 1); this._changed(); }

  /**
   * The print button is live unless a send is in flight or every visible count
   * is zero — a job with nothing in it is a request that can only be rejected.
   */
  public get canPrint(): boolean {
    if (this.status === 'sending' || this.items.length === 0) { return false; }
    return this.items.some(row =>
      (this.printsLabels && row.labelCopies > 0) || (this.printsSheets && row.sheetCopies > 0));
  }

  public onReprintAll(): void {
    if (!this.canPrint) { return; }
    this.reprintAll.emit();
  }

  /** A count changed: re-arm a printed batch and notify the parent. */
  private _changed(): void {
    if (this.status === 'printed') { this.status = 'idle'; }
    this.changed.emit();
  }
}
